/**
 * Slice 41 — Unit onboarding checklist on Overview.
 * Status + deep-link only; never rebuilds Rooms / Inventory / Unit / Access / Settings.
 */

import type { PropertyTabId } from "@/lib/propertyTabs";

export type SetupRowId =
  | "rooms"
  | "cover_photos"
  | "film_guides"
  | "inventory"
  | "thresholds"
  | "unit_items"
  | "appliances"
  | "maintenance"
  | "access"
  | "buy_list"
  | "team";

export type SetupRowStatus = "open" | "done" | "skipped";

export type SetupChecklistState = {
  skipped?: Partial<Record<SetupRowId, true>>;
  thresholdsReviewed?: boolean;
};

export type SetupRowDef = {
  id: SetupRowId;
  n: number;
  title: string;
  hint: string;
  tab: PropertyTabId;
  cta: string;
  skippable: boolean;
  /** Quiet skip confirmation body (phone sheet). */
  skipBody: string;
};

export const SETUP_ROWS: SetupRowDef[] = [
  {
    id: "rooms",
    n: 1,
    title: "Add rooms",
    hint: "Every room a cleaner will walk, in walk order",
    tab: "rooms",
    cta: "Open Rooms",
    skippable: false,
    skipBody: "",
  },
  {
    id: "cover_photos",
    n: 2,
    title: "Room cover photos",
    hint: "The photo cleaners see for each room",
    tab: "rooms",
    cta: "Open Rooms",
    skippable: false,
    skipBody: "",
  },
  {
    id: "film_guides",
    n: 3,
    title: "Film room guides",
    hint: "One short video per room, shot where the cleaner stands",
    tab: "rooms",
    cta: "Open Rooms",
    skippable: false,
    skipBody: "",
  },
  {
    id: "inventory",
    n: 4,
    title: "Build inventory",
    hint: "The supplies you restock: paper, soap, linens, coffee",
    tab: "inventory",
    cta: "Open Inventory",
    skippable: false,
    skipBody: "",
  },
  {
    id: "thresholds",
    n: 5,
    title: "Check low thresholds",
    hint: "Confirm Low when on each supply while you're standing in the unit",
    tab: "inventory",
    cta: "Open Inventory",
    skippable: false,
    skipBody: "",
  },
  {
    id: "unit_items",
    n: 6,
    title: "Add unit items",
    hint: "Baseline kit that should be here after every turnover",
    tab: "unit",
    cta: "Open Unit",
    skippable: true,
    skipBody: "You can add baseline items later. Cleaners won’t have a Unit checklist for this property yet.",
  },
  {
    id: "appliances",
    n: 7,
    title: "Record appliances",
    hint: "Make, model, filter size — photograph the label",
    tab: "unit",
    cta: "Open Unit",
    skippable: true,
    skipBody: "Skip if this unit has no appliances to track. You can add them later from Unit.",
  },
  {
    id: "maintenance",
    n: 8,
    title: "Set maintenance checks",
    hint: "Each check needs a Next due date and a Who",
    tab: "unit",
    cta: "Open Checks",
    skippable: true,
    skipBody: "Skip if you aren’t setting preventative checks today. Due dates can be added later.",
  },
  {
    id: "access",
    n: 9,
    title: "Set access / keys",
    hint: "Door code, lockbox, or a note the cleaner can follow",
    tab: "access",
    cta: "Open Access",
    skippable: true,
    skipBody: "Skip only if cleaners already know how to get in another way.",
  },
  {
    id: "buy_list",
    n: 10,
    title: "Save Amazon buy list",
    hint: "Paste the list URL so restocks go out in one tap",
    tab: "settings",
    cta: "Open Settings",
    skippable: true,
    skipBody: "Restock Open list won’t work until a buy list URL is saved.",
  },
  {
    id: "team",
    n: 11,
    title: "Invite cleaners & team",
    hint: "At least one cleaner before the first turnover",
    tab: "settings",
    cta: "Open Settings",
    skippable: true,
    skipBody: "You can invite cleaners later — jobs will stay unassigned until then.",
  },
];

export function parseSetupChecklistState(raw: unknown): SetupChecklistState {
  if (!raw || typeof raw !== "object") return {};
  const o = raw as Record<string, unknown>;
  const skipped: SetupChecklistState["skipped"] = {};
  const src = o.skipped;
  if (src && typeof src === "object") {
    for (const row of SETUP_ROWS) {
      if ((src as Record<string, unknown>)[row.id] === true) skipped[row.id] = true;
    }
  }
  return {
    skipped: Object.keys(skipped).length ? skipped : undefined,
    thresholdsReviewed: o.thresholdsReviewed === true,
  };
}

export function itemHasLowWhen(item: {
  quantity_type?: string | null;
  threshold_count?: number | null;
  threshold_value?: number | null;
}): boolean {
  const qty = (item.quantity_type || "units").toLowerCase();
  if (qty === "fullness") return item.threshold_value != null;
  return item.threshold_count != null;
}

export type SetupFacts = {
  roomCount: number;
  /** Rooms with a saved cover photo (Slice 52). */
  roomsWithCoverCount: number;
  /** Rooms that have been filmed/captured (items or guide video). */
  roomsFilmedCount: number;
  inventoryCount: number;
  inventoryMissingThreshold: number;
  unitItemCount: number;
  applianceCount: number;
  /** Checks with next due + who. */
  maintenanceReadyCount: number;
  hasAccess: boolean;
  hasBuyList: boolean;
  teamMemberCount: number;
  state: SetupChecklistState;
};

export type SetupRowView = SetupRowDef & {
  status: SetupRowStatus;
  /** Extra teal note under hint (e.g. thresholds remaining). */
  note: string | null;
};

export function evaluateSetupChecklist(facts: SetupFacts): {
  rows: SetupRowView[];
  doneCount: number;
  skippedCount: number;
  complete: boolean;
  openCount: number;
} {
  const dataDone: Record<SetupRowId, boolean> = {
    rooms: facts.roomCount >= 1,
    cover_photos: facts.roomCount >= 1 && facts.roomsWithCoverCount >= facts.roomCount,
    film_guides: facts.roomCount >= 1 && facts.roomsFilmedCount >= facts.roomCount,
    inventory: facts.inventoryCount >= 1,
    thresholds:
      facts.inventoryCount >= 1 &&
      (facts.inventoryMissingThreshold === 0 || facts.state.thresholdsReviewed === true),
    unit_items: facts.unitItemCount >= 1,
    appliances: facts.applianceCount >= 1,
    maintenance: facts.maintenanceReadyCount >= 1,
    access: facts.hasAccess,
    buy_list: facts.hasBuyList,
    team: facts.teamMemberCount >= 1,
  };

  const rows: SetupRowView[] = SETUP_ROWS.map((def) => {
    let status: SetupRowStatus = "open";
    if (dataDone[def.id]) status = "done";
    else if (def.skippable && facts.state.skipped?.[def.id]) status = "skipped";

    let note: string | null = null;
    if (def.id === "thresholds" && status === "open" && facts.inventoryCount > 0) {
      const missing = facts.inventoryMissingThreshold;
      note = `${missing} of ${facts.inventoryCount} supplies have no Low when`;
    }

    // Slice 52: this row's line is the live count, the same line the Rooms tab shows.
    const withCover = Math.min(facts.roomsWithCoverCount, facts.roomCount);
    const view: SetupRowDef =
      def.id === "cover_photos" && facts.roomCount > 0
        ? { ...def, hint: `${withCover} of ${facts.roomCount} rooms are set` }
        : def;

    return { ...view, status, note };
  });

  const doneCount = rows.filter((r) => r.status === "done").length;
  const skippedCount = rows.filter((r) => r.status === "skipped").length;
  const openCount = rows.filter((r) => r.status === "open").length;
  const complete = openCount === 0;

  return { rows, doneCount, skippedCount, complete, openCount };
}

export function setupCompleteSummary(doneCount: number, skippedCount: number): string {
  if (skippedCount <= 0) return `Setup complete · ${doneCount} done`;
  return `Setup complete · ${doneCount} done, ${skippedCount} skipped`;
}

export function setupProgressLabel(doneSettled: number, total = SETUP_ROWS.length): string {
  return `${doneSettled} of ${total} done`;
}

export function setupSubtitle(openCount: number, complete: boolean): string {
  if (complete) return "Finish these on your first visit";
  if (openCount === 1) return "One left — finish it before you leave";
  return "Finish these on your first visit";
}

export function isUntitledPropertyName(name: string | null | undefined): boolean {
  const n = (name || "").trim();
  if (!n) return true;
  return /^untitled(\s+property)?$/i.test(n);
}
